import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { ToastrService } from 'ngx-toastr';
import { catchError, forkJoin, of } from 'rxjs';
import {
  ReturnPolicy,
  ReturnPolicyService,
  UpsertReturnPolicyPayload,
} from './return-policy.service';

interface Lookup {
  id: string;
  name: string;
}

/**
 * Lane B page — Return Policy editor. ADMIN edits the global default;
 * STORE_MANAGER edits the store-specific policy that beats it.
 *
 * Layout (matches the existing offers / GRN form rhythm):
 *   • Header strip with store picker + Save / Reset buttons
 *   • Section 1: Return windows (days)
 *   • Section 2: Refund authority caps (₹) with cap-ladder hint
 *   • Section 3: Behavioral knobs (offer locks, cross-day cash, cross-store)
 *   • Section 4: Blocked categories + departments (multi-select chips)
 *   • History strip (collapsible) showing prior versions
 *
 * Form is dirty-aware so the Save button stays disabled when nothing's
 * changed. Cap-ladder is enforced client-side too so the cashier doesn't
 * get a backend 400 for an obviously-wrong cap.
 */
@Component({
  selector: 'app-return-policy',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, NgSelectModule],
  templateUrl: './return-policy.component.html',
  styleUrl: './return-policy.component.scss',
})
export class ReturnPolicyComponent implements OnInit {
  private svc = inject(ReturnPolicyService);
  private toastr = inject(ToastrService);

  // Picker state
  stores = signal<Lookup[]>([]);
  categories = signal<Lookup[]>([]);
  departments = signal<Lookup[]>([]);
  selectedStoreId = signal<string | null>(null);

  // Current policy in the editor.
  policy = signal<UpsertReturnPolicyPayload>(this.defaultPolicy());
  // The last loaded server-side row — drives dirty-check + history reset.
  baseline = signal<UpsertReturnPolicyPayload>(this.defaultPolicy());
  baselineId = signal<string | null>(null);

  isLoading = signal(false);
  isSaving = signal(false);
  history = signal<ReturnPolicy[]>([]);
  showHistory = signal(false);

  readonly isDirty = computed(() => {
    return JSON.stringify(this.policy()) !== JSON.stringify(this.baseline());
  });

  readonly ladderError = computed(() => {
    const p = this.policy();
    if (p.cashierRefundCap > p.supervisorRefundCap) {
      return `Cashier cap (₹${p.cashierRefundCap}) cannot exceed supervisor cap (₹${p.supervisorRefundCap}).`;
    }
    if (p.supervisorRefundCap > p.managerRefundCap) {
      return `Supervisor cap (₹${p.supervisorRefundCap}) cannot exceed manager cap (₹${p.managerRefundCap}).`;
    }
    return null;
  });

  ngOnInit(): void {
    this.bootstrap();
  }

  bootstrap(): void {
    this.isLoading.set(true);
    forkJoin({
      stores: this.svc.getStores().pipe(catchError(() => of([]))),
      categories: this.svc.getCategories().pipe(catchError(() => of([]))),
      departments: this.svc.getDepartments().pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ stores, categories, departments }) => {
        this.stores.set(this.toLookupArray(stores));
        this.categories.set(this.toLookupArray(categories));
        this.departments.set(this.toLookupArray(departments));
        this.loadPolicy();
      },
      error: () => {
        this.isLoading.set(false);
        this.toastr.error('Failed to load lookups.');
      },
    });
  }

  onStoreChange(storeId: string | null): void {
    this.selectedStoreId.set(storeId);
    this.loadPolicy();
  }

  loadPolicy(): void {
    this.isLoading.set(true);
    this.svc.getActive(this.selectedStoreId()).subscribe({
      next: (row) => {
        const initial = row ? this.fromServer(row) : this.defaultPolicy(this.selectedStoreId());
        this.policy.set(initial);
        this.baseline.set(structuredClone(initial));
        this.baselineId.set(row?.id ?? null);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toastr.error('Failed to load policy.');
      },
    });
  }

  loadHistory(): void {
    this.svc.history(this.selectedStoreId()).subscribe({
      next: (rows) => {
        this.history.set(rows);
        this.showHistory.set(true);
      },
      error: () => this.toastr.error('Failed to load history.'),
    });
  }

  hideHistory(): void {
    this.showHistory.set(false);
  }

  resetToBaseline(): void {
    this.policy.set(structuredClone(this.baseline()));
  }

  save(form: NgForm): void {
    if (this.ladderError()) {
      this.toastr.error(this.ladderError()!);
      return;
    }
    if (!form.valid) {
      this.toastr.error('Please fix the highlighted fields.');
      return;
    }

    this.isSaving.set(true);
    this.svc.upsert(this.policy()).subscribe({
      next: (saved) => {
        this.toastr.success('Policy saved. Active immediately on all terminals after their next sync.');
        const initial = this.fromServer(saved);
        this.policy.set(initial);
        this.baseline.set(structuredClone(initial));
        this.baselineId.set(saved.id);
        this.isSaving.set(false);
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toastr.error(err?.error?.message || 'Save failed.');
      },
    });
  }

  // Wire ng-model to a signal — signals don't have setters that ngModel
  // can two-way bind to natively, so use these small adapters.
  patch<K extends keyof UpsertReturnPolicyPayload>(
    key: K,
    value: UpsertReturnPolicyPayload[K],
  ): void {
    this.policy.update((p) => ({ ...p, [key]: value }));
  }

  // ── private ──────────────────────────────────────────────────────

  private defaultPolicy(storeId: string | null = null): UpsertReturnPolicyPayload {
    return {
      storeId,
      name: 'default',
      windowDaysStandard: 7,
      windowDaysPerishable: 0,
      windowDaysSaleItem: 0,
      cashierRefundCap: 2000,
      supervisorRefundCap: 10000,
      managerRefundCap: 100000,
      allowOfferLockedReturn: false,
      requireOfferOverridePin: true,
      allowCrossDayCashRefund: false,
      allowCrossStoreReturn: false,
      isReturnsEnabled: false,
      blockedCategoryIds: [],
      blockedDepartmentIds: [],
      blockedBrandIds: [],
    };
  }

  private fromServer(row: ReturnPolicy): UpsertReturnPolicyPayload {
    return {
      storeId: row.storeId ?? this.selectedStoreId(),
      name: row.name,
      windowDaysStandard: row.windowDaysStandard,
      windowDaysPerishable: row.windowDaysPerishable,
      windowDaysSaleItem: row.windowDaysSaleItem,
      cashierRefundCap: row.cashierRefundCap,
      supervisorRefundCap: row.supervisorRefundCap,
      managerRefundCap: row.managerRefundCap,
      allowOfferLockedReturn: row.allowOfferLockedReturn,
      requireOfferOverridePin: row.requireOfferOverridePin,
      allowCrossDayCashRefund: row.allowCrossDayCashRefund,
      allowCrossStoreReturn: row.allowCrossStoreReturn,
      isReturnsEnabled: row.isReturnsEnabled ?? false,
      blockedCategoryIds: row.blockedCategories.map((b) => b.id),
      blockedDepartmentIds: row.blockedDepartments.map((b) => b.id),
      blockedBrandIds: row.blockedBrands.map((b) => b.id),
    };
  }

  /**
   * Lookup endpoints in this repo come back as raw arrays OR as { data: [] }
   * depending on the controller. Coerce defensively so the form never
   * blows up on a shape mismatch.
   */
  private toLookupArray(payload: any): Lookup[] {
    const arr: any[] = Array.isArray(payload) ? payload : payload?.data ?? [];
    return arr
      .map((item) => ({ id: item.id ?? item._id, name: item.name ?? item.label }))
      .filter((it) => it.id && it.name);
  }
}
